import { useState } from "react";
import { Loader2, Receipt, Info } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { rupiah, fmtDate } from "@/lib/format";
import { termDue } from "@/components/finance/termDue";
import { usePaymentTerms, findTerm, TermSelect, TermHint } from "@/components/finance/TermSelect";

export const todayStr = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);

export function BillDialog({ row, onClose, onSaved }) {
  const terms = usePaymentTerms();
  const [invDate, setInvDate] = useState(todayStr());
  const [termId, setTermId] = useState(row.ar_term_odoo_id || null);
  const [saving, setSaving] = useState(false);
  const lines = row.lines.filter((l) => l.to_bill_qty > 0);
  const term = findTerm(terms, termId);
  const byOdoo = !row.customer_in_app && !termId && row.odoo_synced;
  const due = term ? termDue(invDate, term.lines, term.days || 0) : termDue(invDate, row.ar_term_lines, row.ar_term_days || 0);
  const placeholder = row.customer_in_app ? "Tunai / Immediate" : "Ikuti termin di Odoo";

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/finance/invoices", { sales_id: row.sales_id, invoice_date: invDate, term_odoo_id: termId || null });
      const d = r.data.data;
      toast.success(`Invoice ${d.number} dibuat${r.data.meta?.odoo_synced ? " & diposting di Odoo" : ""}`);
      if (r.data.meta?.term_warning) toast.warning(r.data.meta.term_warning);
      onSaved(d);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="fin-dialog" title={`Tagihkan ${row.so_number}`} subtitle={row.outlet} testId="bill-dialog"
      footer={<>
        <button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="bill-cancel-btn">Batal</button>
        <button className="rj-btn rj-btn-primary" onClick={submit} disabled={saving} data-testid="bill-submit-btn">
          {saving ? <Loader2 size={16} className="spin" /> : <><Receipt size={16} /> Buat Invoice</>}
        </button>
      </>}>
      <div className="fin-info"><Info size={15} />
        <span>{row.odoo_synced ? "Invoice dibuat & diposting langsung di Odoo, tertaut ke Sales Order." : "Order belum ada di Odoo — invoice dicatat di Sales Hub saja."} Hanya qty yang sudah dikirim dan belum ditagih yang masuk invoice.</span>
      </div>
      <div className="fin-form-grid">
        <label className="rj-field">Tanggal Penagihan
          <input type="date" className="rj-input" value={invDate} onChange={(e) => setInvDate(e.target.value || todayStr())} data-testid="bill-invoice-date" />
        </label>
        <label className="rj-field">AR Term invoice
          <TermSelect terms={terms} value={termId} onChange={setTermId} placeholder={placeholder} testId="bill-ar-term-select" />
        </label>
        <div className="fin-span"><TermHint row={row} termId={termId} terms={terms} testId="bill-ar-term-hint" /></div>
        <div className="fin-kv"><span>Perkiraan jatuh tempo</span><b data-testid="bill-due-date">{byOdoo ? "Ditentukan Odoo" : fmtDate(due)}</b></div>
        <div className="fin-kv"><span>DO</span><b>{row.shipments.map((s) => `${s.picking} (${fmtDate(s.date)})`).join(", ") || "—"}</b></div>
      </div>
      <div className="fin-lines">
        <div className="fin-line head"><span>Produk</span><span>Dikirim</span><span>Sudah ditagih</span><span>Ditagih</span><span>Subtotal</span></div>
        {lines.map((l) => (
          <div className="fin-line" key={l.product_odoo_id} data-testid={`bill-line-${l.product_odoo_id}`}>
            <strong>{l.product_name}</strong><span>{l.shipped_qty}</span><span>{l.invoiced_qty}</span>
            <b>{l.to_bill_qty} {l.uom || ""}</b><span>{rupiah(l.to_bill_qty * l.unit_price)}</span>
          </div>
        ))}
      </div>
      <div className="activity-totals">
        <div><span>DPP</span><span>{rupiah(row.estimate.untaxed_amount)}</span></div>
        <div><span>PPN ({(row.tax_rate_bps || 0) / 100}% · {row.tax_mode || "-"})</span><span>{rupiah(row.estimate.tax_amount)}</span></div>
        <div className="grand"><span>Estimasi total tagihan</span><strong data-testid="bill-total">{rupiah(row.estimate.amount)}</strong></div>
      </div>
    </Modal>
  );
}
